import { useState, useEffect, useCallback } from "react";
import { X, Info, AlertTriangle, AlertCircle, CheckCircle } from "lucide-react";
import styles from "./CustomInfoMessage.module.scss";

const ICON_MAP = {
	info: Info,
	warning: AlertTriangle,
	error: AlertCircle,
	success: CheckCircle,
};

const CustomInfoMessage = ({ messages = [], type = "info", title, autoCloseDuration = 30000, onClose }) => {
	const [visibleMessages, setVisibleMessages] = useState([]);
	const [progress, setProgress] = useState(100);
	const [shouldClose, setShouldClose] = useState(false);

	useEffect(() => {
		if (messages.length > 0) {
			const newMessages = messages.map((msg, index) => ({
				id: `${Date.now()}-${index}`,
				text: msg,
				dismissed: false,
			}));
			setVisibleMessages(newMessages);
			setProgress(100);
			setShouldClose(false);
		}
	}, [messages]);

	const handleCloseAll = useCallback(() => {
		setVisibleMessages([]);
		setShouldClose(true);
	}, []);

	useEffect(() => {
		if (shouldClose && onClose) {
			onClose();
		}
	}, [shouldClose, onClose]);

	useEffect(() => {
		if (visibleMessages.length === 0) return;

		const interval = setInterval(() => {
			setProgress((prev) => {
				const newProgress = prev - 100 / (autoCloseDuration / 100);
				if (newProgress <= 0) {
					handleCloseAll();
					return 0;
				}
				return newProgress;
			});
		}, 100);

		return () => clearInterval(interval);
	}, [visibleMessages, autoCloseDuration, handleCloseAll]);

	if (visibleMessages.length === 0) return null;

	const Icon = ICON_MAP[type] || Info;
	const resolvedTitle = title ? title : type === "info" ? "Información" : type === "warning" ? "Advertencia" : type === "error" ? "Error" : "Éxito";

	return (
		<div className={`${styles.container} ${styles[type]}`}>
			<div className={styles.header}>
				<div className={styles.iconTitle}>
					<Icon className={styles.icon} />
					<span className={styles.title}>{resolvedTitle}</span>
				</div>
				<button onClick={handleCloseAll} className={styles.closeButton} title="Cerrar">
					<X size={18} />
				</button>
			</div>

			<div className={styles.messagesContainer}>
				{visibleMessages.map((message) => (
					<div key={message.id} className={styles.message}>
						<span className={styles.messageText}>{message.text}</span>
					</div>
				))}
			</div>

			<div className={styles.progressBar}>
				<div className={styles.progressFill} style={{ width: `${progress}%` }} />
			</div>
		</div>
	);
};

export default CustomInfoMessage;
